import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, Settings, Fish, LayoutGrid, Users, Plus, Trash2, Key, Eye, EyeOff, Save, Check, User, RotateCcw, Pencil, Search, CalendarCheck,
  Copy, Link2,
  Carrot, Apple, Pizza, Coffee, Utensils, Cake, IceCream, Grape, Banana, Cherry, Croissant, Sandwich, Cookie, Drumstick, GlassWater, Popcorn,
  Ruler, Book, Notebook, Paperclip, Scissors, Highlighter, Pen, Pin,
  Gamepad2, Music, Headphones, Tv, Ticket, Dices, Ghost, Skull, Puzzle, Palette,
  Crown, Star, Trophy, Medal, Award, Sparkles, GraduationCap,
  Smile, Zap, Lightbulb, Bell, Flag, Clock, Calendar, Briefcase, Calculator, Folder,
  Dumbbell, Bike, Activity, Target, Map, Compass,
  Snowflake, Flame, Droplets, Flower, Leaf, TreeDeciduous, Mountain, Sun, Moon, Cloud,
  Cat, Dog, Rabbit, Bird, Snail, Turtle,
  Rocket, Plane, Car, Bus, Truck, Ship, Gift, Package, Lock, Unlock, Heart, Import,
  Layers, AlertTriangle, GripVertical, Loader2, Download, Monitor
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import api from '../lib/api';
import {
  defaultPetLifeStateConfig,
  parsePetLifeStateConfig,
  validatePetLifeStateConfig,
  type PetLifeStateConfig,
} from '../lib/petUtils';

function SortableItem({ id, children }: { id: number; children: React.ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    position: 'relative' as const,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <div {...attributes} {...listeners} className="p-2 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 rounded-lg hover:bg-gray-100 transition-colors shrink-0 touch-none">
        <GripVertical className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        {children}
      </div>
    </div>
  );
}

import { normalizeLanguage } from '../i18n';
import { useClassStore } from '../store/useClassStore';
import { useAuthStore } from '../store/useAuthStore';
import Toast from './Toast';

// Icon Mapping

// Icon Mapping
const ICON_MAP: Record<string, React.ElementType> = {
  // Food
  Carrot, Apple, Pizza, Coffee, Utensils, Cake, IceCream, Grape, Banana, Cherry, Croissant, Sandwich, Cookie, Drumstick, GlassWater, Popcorn,
  // Stationery
  Pencil, Ruler, Book, Notebook, Paperclip, Scissors, Highlighter, Folder, Calculator, Pen, Pin,
  // Entertainment
  Gamepad2, Music, Headphones, Tv, Ticket, Dices, Ghost, Skull, Puzzle, Palette,
  // Privilege/Awards
  Crown, Star, Trophy, Medal, Award, Sparkles, GraduationCap, Gift, Package,
  // Sports/Transport
  Dumbbell, Bike, Activity, Target, Rocket, Plane, Car, Bus, Truck, Ship, Map, Compass,
  // Nature/Animals
  Snowflake, Flame, Droplets, Flower, Leaf, TreeDeciduous, Mountain, Sun, Moon, Cloud,
  Cat, Dog, Rabbit, Bird, Snail, Turtle, Fish,
  // Misc
  Smile, Zap, Lightbulb, Bell, Flag, Clock, Calendar, Briefcase, Key, Lock, Unlock, Heart,
  // UI
  Check, X, Plus, Trash2, Settings, Users, LayoutGrid, User, RotateCcw, Search
};

const ICON_CATEGORIES = [
  { id: 'honor', nameKey: 'settings.icons.honor', icons: ['Crown', 'Star', 'Trophy', 'Medal', 'Award', 'Sparkles', 'GraduationCap', 'Gift', 'Package', 'Heart', 'Smile', 'Zap', 'Flag'] },
  { id: 'study', nameKey: 'settings.icons.study', icons: ['Pencil', 'Ruler', 'Book', 'Notebook', 'Paperclip', 'Scissors', 'Highlighter', 'Folder', 'Calculator', 'Pen', 'Pin', 'Lightbulb', 'Bell', 'Clock', 'Calendar', 'Briefcase'] },
  { id: 'activity', nameKey: 'settings.icons.activity', icons: ['Gamepad2', 'Music', 'Headphones', 'Tv', 'Ticket', 'Dices', 'Ghost', 'Skull', 'Puzzle', 'Palette', 'Dumbbell', 'Bike', 'Activity', 'Target'] },
  { id: 'nature', nameKey: 'settings.icons.nature', icons: ['Sun', 'Moon', 'Cloud', 'Snowflake', 'Flame', 'Droplets', 'Flower', 'Leaf', 'TreeDeciduous', 'Mountain', 'Cat', 'Dog', 'Rabbit', 'Bird', 'Snail', 'Turtle', 'Fish'] },
  { id: 'food', nameKey: 'settings.icons.food', icons: ['Carrot', 'Apple', 'Pizza', 'Coffee', 'Utensils', 'Cake', 'IceCream', 'Grape', 'Banana', 'Cherry', 'Croissant', 'Sandwich', 'Cookie', 'Drumstick', 'GlassWater', 'Popcorn'] },
  { id: 'life', nameKey: 'settings.icons.life', icons: ['Rocket', 'Plane', 'Car', 'Bus', 'Truck', 'Ship', 'Map', 'Compass', 'Key', 'Lock', 'Unlock'] },
];

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  onClassesUpdate?: () => void;
  onGrowthUpdate?: () => void;
  initialTab?: 'parentShare' | 'account' | 'growth' | 'petLife' | 'items' | 'students' | 'checkin';
  classes: { id: number; name: string; students?: { id: number }[]; studentCount?: number }[];
}

interface CheckinTask {
  id: number;
  name: string;
  points: number;
  icon: string | null;
  enabled: boolean;
  order: number;
  startDate?: string | null;
  endDate?: string | null;
  repeatDays?: number;
}

interface CheckinTaskFormState {
  id?: number; // present = edit mode, otherwise add mode
  name: string;
  points: number;
  startDate: string; // '' = no limit
  endDate: string;
  repeatDays: number; // bitmask: bit0=Sun, bit1=Mon, ..., bit6=Sat
}

// Display order: Mon → Sun (Chinese convention). bit numbers per JS Date.getDay().
const CHECKIN_WEEKDAY_DEFS: { bit: number; key: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun' }[] = [
  { bit: 1, key: 'mon' },
  { bit: 2, key: 'tue' },
  { bit: 3, key: 'wed' },
  { bit: 4, key: 'thu' },
  { bit: 5, key: 'fri' },
  { bit: 6, key: 'sat' },
  { bit: 0, key: 'sun' },
];
const CHECKIN_REPEAT_ALL = 0b1111111; // 127
const CHECKIN_REPEAT_WEEKDAYS = 0b0111110; // 62 = Mon-Fri
const CHECKIN_REPEAT_WEEKEND = 0b1000001; // 65 = Sat + Sun

type ClassSettingsState = {
  isSoundEnabled?: boolean;
  isAttenuationEnabled?: boolean;
};

const DEFAULT_GROWTH_CONFIG = [0, 5, 10, 20, 30, 45, 60, 75, 90, 100];

const normalizeGrowthConfig = (config: number[]) => config.map((value, index) => index === 0 ? 0 : Number(value) || 0);

const areClassSettingsEqual = (a: ClassSettingsState, b: ClassSettingsState) => (
  (a.isSoundEnabled ?? false) === (b.isSoundEnabled ?? false) &&
  (a.isAttenuationEnabled ?? false) === (b.isAttenuationEnabled ?? false)
);

const arePetLifeConfigsEqual = (a: PetLifeStateConfig, b: PetLifeStateConfig) => (
  a.hungryAfterDays === b.hungryAfterDays &&
  a.weakAfterDays === b.weakAfterDays &&
  a.dyingAfterDays === b.dyingAfterDays &&
  a.sleepAfterDays === b.sleepAfterDays &&
  (a.hungryLabel || '') === (b.hungryLabel || '') &&
  (a.weakLabel || '') === (b.weakLabel || '') &&
  (a.dyingLabel || '') === (b.dyingLabel || '') &&
  (a.deadLabel || '') === (b.deadLabel || '') &&
  !!a.skipWeekends === !!b.skipWeekends
);

interface PointItem {
  id: number;
  name: string;
  points: number;
  icon: string;
  order?: number;
}

interface Student {
  id: number;
  name: string;
  studentNumber: string;
  groupIds?: number[];
}

export default function SettingsModal({ isOpen, onClose, onUpdate, onClassesUpdate, onGrowthUpdate, initialTab = 'growth', classes }: SettingsModalProps) {
  const { currentClass, setCurrentClass } = useClassStore();
  const { user, logout } = useAuthStore();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'parentShare' | 'account' | 'growth' | 'petLife' | 'items' | 'students' | 'checkin'>(initialTab);
  const [validUntil, setValidUntil] = useState<string | null>(null);
  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      void 0;
    } finally {
      logout();
      setCurrentClass(null);
    }
  };

  const storedUsername = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return '';
      const parsed = JSON.parse(raw) as { username?: unknown };
      return typeof parsed.username === 'string' ? parsed.username : '';
    } catch {
      return '';
    }
  })();
  const displayUsername = user?.username || storedUsername || 'Teacher';
  const avatarLetter = displayUsername?.charAt(0).toUpperCase() || 'G';
  const activeLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  const formatValidUntil = (value: string | null) => {
    if (!value) return t('settings.license.permanent');
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return t('settings.license.permanent');
    const parts = new Intl.DateTimeFormat(activeLanguage, {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(d);
    const map: Record<string, string> = {};
    for (const p of parts) {
      if (p.type !== 'literal') map[p.type] = p.value;
    }
    return t('settings.license.validUntil', { date: `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}` });
  };

  const fetchTeacherProfile = async () => {
    try {
      const res = await api.get('/teachers/me');
      const v = res.data?.validUntil;
      setValidUntil(typeof v === 'string' ? v : v ? String(v) : null);
    } catch {
      setValidUntil(null);
    }
  };

  // Account & System Settings
  const [className, setClassName] = useState('');
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [isCreatingClass, setIsCreatingClass] = useState(false);
  const [parentShareEnabled, setParentShareEnabled] = useState(true);
  const [parentShareLink, setParentShareLink] = useState('');
  const [isParentShareLinkLoading, setIsParentShareLinkLoading] = useState(false);
  
  // Growth Config
  const [growthConfig, setGrowthConfig] = useState<number[]>([0, 5, 10, 20, 30, 45, 60, 75, 90, 100]);
  const [petLifeConfig, setPetLifeConfig] = useState<PetLifeStateConfig>(defaultPetLifeStateConfig);
  const [petLifeConfigError, setPetLifeConfigError] = useState('');
  const [classSettings, setClassSettings] = useState<ClassSettingsState>({
    isSoundEnabled: false,
    isAttenuationEnabled: false,
  });
  const [savedGrowthConfig, setSavedGrowthConfig] = useState<number[]>(DEFAULT_GROWTH_CONFIG);
  const [savedPetLifeConfig, setSavedPetLifeConfig] = useState<PetLifeStateConfig>(defaultPetLifeStateConfig);
  const [savedClassSettings, setSavedClassSettings] = useState<ClassSettingsState>({
    isSoundEnabled: false,
    isAttenuationEnabled: false,
  });
  const [savingClassSettingKey, setSavingClassSettingKey] = useState<keyof ClassSettingsState | null>(null);
  
  // Theme
  // const [themeColor, setThemeColor] = useState('pink');

  // Point Items
  const [pointItems, setPointItems] = useState<PointItem[]>([]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemPoints, setNewItemPoints] = useState(1);
  const [newItemIcon, setNewItemIcon] = useState('Star');
  const [itemsSubTab, setItemsSubTab] = useState<'bonus' | 'deduction'>('bonus');
  const [editingItem, setEditingItem] = useState<PointItem | null>(null);

  // Check-in Tasks
  const [checkinTasks, setCheckinTasks] = useState<CheckinTask[]>([]);
  const [checkinTaskForm, setCheckinTaskForm] = useState<CheckinTaskFormState | null>(null);
  const [checkinFormSubmitting, setCheckinFormSubmitting] = useState(false);
  const [checkinDeleteConfirm, setCheckinDeleteConfirm] = useState<CheckinTask | null>(null);
  const [isCheckinDeleting, setIsCheckinDeleting] = useState(false);
  
  // Icon Picker
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<'new' | 'edit'>('new');
  const [activeIconCategory, setActiveIconCategory] = useState('food');

  // Students
  const [students, setStudents] = useState<Student[]>([]);
  const [newStudentName, setNewStudentName] = useState('');
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);

  // Password Modal
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Delete Confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ id: number; name: string } | null>(null);

  const [classDeleteConfirmation, setClassDeleteConfirmation] = useState<{ id: number; name: string } | null>(null);
  const [classDeletePassword, setClassDeletePassword] = useState('');
  const [classDeleteError, setClassDeleteError] = useState('');
  const [isClassDeleting, setIsClassDeleting] = useState(false);
  
  // Assign Confirmation
  const [assignConfirmation, setAssignConfirmation] = useState(false);

  // Image Preview
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Local Classes for sorting
  const [localClasses, setLocalClasses] = useState(classes);

  useEffect(() => {
    setLocalClasses(classes);
  }, [classes]);

  const handleClassDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setLocalClasses((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        
        if (oldIndex === -1 || newIndex === -1) return items;

        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Save order to backend
        const reorderedItems = newItems.map((item, index) => ({
             id: item.id,
             order: index
        }));
        
        api.post('/classes/reorder', reorderedItems).then(() => {
             onClassesUpdate?.();
        }).catch(err => console.error(err));
        
        return newItems;
      });
    }
  };

  // Reset Class
  const [resetClassConfirmation, setResetClassConfirmation] = useState(false);
  const [resetClassPassword, setResetClassPassword] = useState('');
  const [resetClassError, setResetClassError] = useState('');
  const [isResettingClass, setIsResettingClass] = useState(false);
  const [resetCountdown, setResetCountdown] = useState(3);

  // Import Config
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importTargetTab, setImportTargetTab] = useState<'growth' | 'petLife' | 'items'>('growth');
  const [selectedImportClassId, setSelectedImportClassId] = useState<number | ''>('');
  const [isSystemTemplateModalOpen, setIsSystemTemplateModalOpen] = useState(false);
  const [isImportingSystemTemplate, setIsImportingSystemTemplate] = useState(false);
  const [systemTemplateAgeRange, setSystemTemplateAgeRange] = useState<'3-5' | '6-9' | '10+'>('6-9');

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      fetchTeacherProfile();
    }
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
      fetchPointItems();
      fetchCheckinTasks();
      if (currentClass) {
        setClassName(currentClass.name);
        setParentShareEnabled(currentClass.parentShareEnabled ?? false);
        setParentShareLink('');
        fetchStudents();
      }
    }
  }, [isOpen, currentClass]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (resetClassConfirmation) {
      setResetCountdown(3);
      timer = setInterval(() => {
        setResetCountdown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resetClassConfirmation]);

  const fetchSettings = async () => {
    if (!currentClass) return;
    try {
      const [growthRes, petLifeRes] = await Promise.all([
        api.get(`/settings/key/growth_config?classId=${currentClass.id}`),
        api.get(`/settings/key/pet_life_state_config?classId=${currentClass.id}`),
      ]);

      if (growthRes.data) {
        const config = JSON.parse(growthRes.data.value);
        if (Array.isArray(config) && config.length > 0) {
          config[0] = 0;
        }
        const normalized = normalizeGrowthConfig(config);
        setGrowthConfig(normalized);
        setSavedGrowthConfig(normalized);
      }
      else {
        // Reset to default if not found (important when switching classes)
        setGrowthConfig(DEFAULT_GROWTH_CONFIG);
        setSavedGrowthConfig(DEFAULT_GROWTH_CONFIG);
      }

      const parsedPetLife = parsePetLifeStateConfig(petLifeRes.data?.value);
      setPetLifeConfig(parsedPetLife);
      setSavedPetLifeConfig(parsedPetLife);
      const nextClassSettings = {
        isSoundEnabled: currentClass.isSoundEnabled ?? false,
        isAttenuationEnabled: currentClass.isAttenuationEnabled ?? false,
      };
      setClassSettings(nextClassSettings);
      setSavedClassSettings(nextClassSettings);
      setPetLifeConfigError('');
    } catch (error) {
      console.error('Failed to fetch settings', error);
      // Fallback to default
      setGrowthConfig(DEFAULT_GROWTH_CONFIG);
      setSavedGrowthConfig(DEFAULT_GROWTH_CONFIG);
      setPetLifeConfig(defaultPetLifeStateConfig);
      setSavedPetLifeConfig(defaultPetLifeStateConfig);
      const fallbackClassSettings = {
        isSoundEnabled: currentClass.isSoundEnabled ?? false,
        isAttenuationEnabled: currentClass.isAttenuationEnabled ?? false,
      };
      setClassSettings(fallbackClassSettings);
      setSavedClassSettings(fallbackClassSettings);
      setPetLifeConfigError('');
    }
  };

  const fetchPointItems = async () => {
    if (!currentClass) return;
    try {
      const response = await api.get(`/point-items?classId=${currentClass.id}`);
      setPointItems(response.data);
    } catch (error) {
      console.error('Failed to fetch point items', error);
      setPointItems([]);
    }
  };

  const fetchCheckinTasks = async () => {
    if (!currentClass) return;
    try {
      // Use the management endpoint so the settings panel always shows ALL
      // tasks (the /checkin/today endpoint filters by date range / weekday).
      const response = await api.get(`/checkin/tasks?classId=${currentClass.id}`);
      setCheckinTasks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch check-in tasks', error);
      setCheckinTasks([]);
    }
  };

  const openAddCheckinTaskForm = () => {
    setCheckinTaskForm({
      name: '',
      points: 2,
      startDate: '',
      endDate: '',
      repeatDays: CHECKIN_REPEAT_ALL,
    });
  };

  const openEditCheckinTaskForm = (task: CheckinTask) => {
    setCheckinTaskForm({
      id: task.id,
      name: task.name,
      points: task.points,
      startDate: task.startDate ?? '',
      endDate: task.endDate ?? '',
      repeatDays: task.repeatDays ?? CHECKIN_REPEAT_ALL,
    });
  };

  const closeCheckinTaskForm = () => {
    setCheckinTaskForm(null);
  };

  const handleSubmitCheckinTaskForm = async () => {
    if (!checkinTaskForm || !currentClass) return;
    const name = checkinTaskForm.name.trim();
    const points = Number(checkinTaskForm.points);
    if (!name || !Number.isFinite(points) || points < 1) return;
    if (!checkinTaskForm.repeatDays) return; // require at least one weekday
    if (
      checkinTaskForm.startDate &&
      checkinTaskForm.endDate &&
      checkinTaskForm.startDate > checkinTaskForm.endDate
    ) {
      showToast(t('checkin.form.dateRangeInvalid'), 'error');
      return;
    }

    setCheckinFormSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name,
        points,
        startDate: checkinTaskForm.startDate || null,
        endDate: checkinTaskForm.endDate || null,
        repeatDays: checkinTaskForm.repeatDays,
      };
      if (checkinTaskForm.id) {
        await api.patch(`/checkin/tasks/${checkinTaskForm.id}`, payload);
      } else {
        await api.post('/checkin/tasks', { ...payload, classId: currentClass.id });
      }
      setCheckinTaskForm(null);
      await fetchCheckinTasks();
      showToast(t('checkin.toast.taskCreated'), 'success');
      onUpdate?.();
    } catch (error) {
      console.error('Failed to save check-in task', error);
      showToast(t('checkin.toast.taskCreateFailed'), 'error');
    } finally {
      setCheckinFormSubmitting(false);
    }
  };

  const handleConfirmDeleteCheckinTask = async () => {
    if (!checkinDeleteConfirm) return;
    setIsCheckinDeleting(true);
    try {
      await api.delete(`/checkin/tasks/${checkinDeleteConfirm.id}`);
      setCheckinDeleteConfirm(null);
      await fetchCheckinTasks();
      showToast(t('checkin.toast.taskDeleted'), 'success');
      onUpdate?.();
    } catch (error) {
      console.error('Failed to delete check-in task', error);
      showToast(t('checkin.toast.taskDeleteFailed'), 'error');
    } finally {
      setIsCheckinDeleting(false);
    }
  };

  const fetchStudents = async () => {
    if (!currentClass) return;
    try {
      const response = await api.get(`/students?classId=${currentClass.id}`);
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students', error);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 6) {
      setPasswordError(t('settings.password.error.minLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('settings.password.error.mismatch'));
      return;
    }

    try {
      const res = await api.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      // Update token to keep current session alive after password change
      if (res.data?.access_token) {
        localStorage.setItem('token', res.data.access_token);
      }
      setPasswordSuccess(t('settings.password.success'));
      setTimeout(() => {
        setIsPasswordModalOpen(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSuccess('');
      }, 1500);
    } catch (error) {
      console.error('Failed to change password', error);
      setPasswordError(t('settings.password.error.failed'));
    }
  };

  const handleImportConfig = async () => {
    if (!currentClass || !selectedImportClassId) return;
    
    try {
      if (importTargetTab === 'growth' || importTargetTab === 'petLife') {
        await api.post('/settings/import', {
          fromClassId: selectedImportClassId,
          toClassId: currentClass.id
        });
        fetchSettings();
      } else {
        await api.post('/point-items/import', {
          fromClassId: selectedImportClassId,
          toClassId: currentClass.id
        });
        fetchPointItems();
      }
      
      setIsImportModalOpen(false);
      showToast(t('settings.import.success'), 'success');
    } catch (error) {
      console.error('Failed to import config', error);
      showToast(t('settings.import.failed'), 'error');
    }
  };

  const handleImportSystemTemplate = async () => {
    if (!currentClass) return;
    if (isImportingSystemTemplate) return;
    setIsImportingSystemTemplate(true);
    try {
      const payload = {
        toClassId: currentClass.id,
        ageRange: systemTemplateAgeRange,
      };
      // 同步导入三套：行为习惯加减分项、打卡任务、小卖部商品。
      // 三个独立 API 调用，单个失败不影响其他 — 用 allSettled 容错。
      const [pointItemsRes, checkinRes, goodsRes] = await Promise.allSettled([
        api.post('/point-items/import-system-template', payload),
        api.post('/checkin/tasks/import-system-template', payload),
        api.post('/goods/import-system-template', payload),
      ]);
      await fetchPointItems();
      setIsSystemTemplateModalOpen(false);

      const sumCreated = (r: PromiseSettledResult<{ data?: { createdCount?: number } }>) =>
        r.status === 'fulfilled' ? Number(r.value?.data?.createdCount ?? 0) : 0;
      const sumSkipped = (r: PromiseSettledResult<{ data?: { skippedCount?: number } }>) =>
        r.status === 'fulfilled' ? Number(r.value?.data?.skippedCount ?? 0) : 0;

      const createdCount = sumCreated(pointItemsRes) + sumCreated(checkinRes) + sumCreated(goodsRes);
      const skippedCount = sumSkipped(pointItemsRes) + sumSkipped(checkinRes) + sumSkipped(goodsRes);

      showToast(
        t('settings.items.importSystemTemplateToast', { created: createdCount, skipped: skippedCount }),
        'success',
      );
    } catch (error) {
      console.error('Failed to import system template', error);
      showToast(t('settings.items.importSystemTemplateFailed'), 'error');
    } finally {
      setIsImportingSystemTemplate(false);
    }
  };

  const handleSavePetLifeConfig = async (newConfig: PetLifeStateConfig, silent = false): Promise<boolean> => {
    if (!currentClass) return false;
    
    try {
      if (!validatePetLifeStateConfig(newConfig)) {
        setPetLifeConfigError(t('settings.petLife.validation.order'));
        showToast(t('settings.save.failed'), 'error');
        return false;
      }

      await api.post('/settings', {
        key: 'pet_life_state_config',
        value: JSON.stringify(newConfig),
        classId: currentClass.id,
      });
      
      setPetLifeConfig(newConfig);
      setSavedPetLifeConfig(newConfig);
      setPetLifeConfigError('');
      if (!silent) {
        showToast(t('settings.save.success'), 'success');
      }
      return true;
    } catch (error) {
      console.error('Failed to save pet life config', error);
      showToast(t('settings.save.failed'), 'error');
      return false;
    }
  };

  const handleToggleClassSetting = async (
    key: keyof ClassSettingsState,
    value: boolean,
  ) => {
    if (!currentClass) return;

    const previousClassSettings = classSettings;
    const nextClassSettings = {
      ...classSettings,
      [key]: value,
    };

    setClassSettings(nextClassSettings);
    setSavingClassSettingKey(key);

    try {
      await api.put(`/classes/${currentClass.id}`, { [key]: value });
      const updatedClass = { ...currentClass, ...nextClassSettings };
      setCurrentClass(updatedClass);
      setSavedClassSettings(nextClassSettings);
      onClassesUpdate?.();
      if (key === 'isAttenuationEnabled') {
        await onUpdate?.();
      }
      showToast(t('settings.save.success'), 'success');
    } catch (error) {
      console.error('Failed to update class setting', error);
      setClassSettings(previousClassSettings);
      showToast(t('settings.save.failed'), 'error');
    } finally {
      setSavingClassSettingKey(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
      } catch {
        return false;
      }
    }
  };

  const handleToggleParentShareEnabled = async (value: boolean) => {
    if (!currentClass) return;
    const previous = parentShareEnabled;
    setParentShareEnabled(value);
    try {
      await api.put(`/classes/${currentClass.id}`, { parentShareEnabled: value });
      const updatedClass = { ...currentClass, parentShareEnabled: value };
      setCurrentClass(updatedClass);
      onClassesUpdate?.();
      showToast(t('settings.save.success'), 'success');
    } catch (error) {
      console.error('Failed to update parent share setting', error);
      setParentShareEnabled(previous);
      showToast(t('settings.save.failed'), 'error');
    }
  };

  const handleGenerateParentShareLink = async () => {
    if (!currentClass) return;
    setIsParentShareLinkLoading(true);
    try {
      const res = await api.post(`/classes/${currentClass.id}/parent-share-link`);
      const token = res.data?.token;
      if (!token) {
        showToast(t('settings.save.failed'), 'error');
        return;
      }
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const link = `${origin}/share/${token}`;
      setParentShareLink(link);
      showToast(t('settings.parentShare.toast.generated'), 'success');
    } catch (error) {
      console.error('Failed to generate parent share link', error);
      showToast(t('settings.parentShare.toast.generateFailed'), 'error');
    } finally {
      setIsParentShareLinkLoading(false);
    }
  };

  const handleRotateParentShareLink = async () => {
    if (!currentClass) return;
    setIsParentShareLinkLoading(true);
    try {
      const res = await api.post(`/classes/${currentClass.id}/parent-share-rotate`);
      const token = res.data?.token;
      if (!token) {
        showToast(t('settings.save.failed'), 'error');
        return;
      }
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const link = `${origin}/share/${token}`;
      setParentShareLink(link);
      showToast(t('settings.parentShare.toast.rotated'), 'success');
    } catch (error) {
      console.error('Failed to rotate parent share link', error);
      showToast(t('settings.parentShare.toast.rotateFailed'), 'error');
    } finally {
      setIsParentShareLinkLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      let didSave = false;

      if (activeTab === 'account') {
        // await api.post('/settings', { key: 'system_name', value: systemName });
        if (currentClass && className !== currentClass.name) {
          await api.put(`/classes/${currentClass.id}`, { name: className });
          setCurrentClass({ ...currentClass, name: className });
          onClassesUpdate?.();
          didSave = true;
        }
      } else if (activeTab === 'growth' || activeTab === 'petLife') {
        if (currentClass) {
          const classSettingsChanged = !areClassSettingsEqual(classSettings, savedClassSettings);

          if (classSettingsChanged) {
            const updatedClass = { ...currentClass, ...classSettings };
            setCurrentClass(updatedClass);
            setSavedClassSettings(classSettings);
            onClassesUpdate?.();
            didSave = true;
          }

          if (activeTab === 'growth') {
            const normalizedGrowthConfig = normalizeGrowthConfig(growthConfig);

            // Validate that levels are strictly increasing
            for (let i = 1; i < normalizedGrowthConfig.length; i++) {
              if (normalizedGrowthConfig[i] <= normalizedGrowthConfig[i - 1]) {
                showToast(t('settings.growth.errorNotIncreasing', { level: i + 1 }), 'error');
                return;
              }
            }

            const growthChanged = JSON.stringify(normalizedGrowthConfig) !== JSON.stringify(savedGrowthConfig);

            if (growthChanged) {
              await api.post('/settings', {
                key: 'growth_config',
                value: JSON.stringify(normalizedGrowthConfig),
                classId: currentClass.id
              });
              setGrowthConfig(normalizedGrowthConfig);
              setSavedGrowthConfig(normalizedGrowthConfig);
              onGrowthUpdate?.();
              didSave = true;
            }
          } else if (activeTab === 'petLife') {
            const petLifeChanged = !arePetLifeConfigsEqual(petLifeConfig, savedPetLifeConfig);

            if (petLifeChanged) {
              const success = await handleSavePetLifeConfig(petLifeConfig, true);
              if (success) {
                onGrowthUpdate?.();
                didSave = true;
              } else {
                return; // Stop the save process if validation failed
              }
            }
          }
        }
      }
      
      if (!didSave) {
        return;
      }

      if (activeTab !== 'petLife') {
        showToast(t('settings.save.success'), 'success');
      } else {
        showToast(t('settings.save.success'), 'success');
      }
    } catch (error) {
      console.error('Failed to save settings', error);
      showToast(t('settings.save.failed'), 'error');
    }
  };

  // Removed unused handleResetPetLifeConfig

  const handleCreateClass = async () => {
    const name = newClassName.trim();
    if (!name) return;

    setIsCreatingClass(true);
    try {
      type CreatedClass = { id: number; name: string; students?: { id: number }[]; studentCount?: number; isSoundEnabled?: boolean; isAttenuationEnabled?: boolean };
      const response = await api.post('/classes', { name });
      const created = response.data as CreatedClass | undefined;
      setNewClassName('');
      setIsAddingClass(false);
      onClassesUpdate?.();
      if (created?.id && created?.name) {
        setCurrentClass(created);
      }
      showToast(t('settings.class.createSuccess'), 'success');
    } catch (error) {
      console.error('Failed to create class', error);
      showToast(t('settings.class.createFailed'), 'error');
    } finally {
      setIsCreatingClass(false);
    }
  };

  const handleDeleteClass = async () => {
    if (!classDeleteConfirmation || !classDeletePassword.trim()) return;
    setIsClassDeleting(true);
    setClassDeleteError('');
    try {
      await api.delete(`/classes/${classDeleteConfirmation.id}`, {
        data: { password: classDeletePassword },
      });
      const deletedId = classDeleteConfirmation.id;
      setClassDeleteConfirmation(null);
      setClassDeletePassword('');
      setClassDeleteError('');
      showToast(t('settings.class.deleteSuccess'), 'success');
      if (currentClass?.id === deletedId) {
        const next = classes.find((c) => c.id !== deletedId) ?? null;
        setCurrentClass(next);
        setClassName(next?.name ?? '');
      }
      await onClassesUpdate?.();
    } catch (error) {
      const response =
        typeof error === 'object' && error && 'response' in error
          ? (error as { response?: { status?: number; data?: unknown } }).response
          : undefined;
      if (response?.status) {
        setClassDeleteError(t('settings.class.deleteFailedHttp', { status: response.status }));
        return;
      }
      const errorMessage =
        typeof error === 'object' && error && 'message' in error
          ? (error as { message?: unknown }).message
          : undefined;
      if (typeof errorMessage === 'string' && errorMessage) {
        setClassDeleteError(t('settings.class.deleteNetworkError', { message: errorMessage }));
        return;
      }
      setClassDeleteError(t('settings.class.deleteFailed'));
    } finally {
      setIsClassDeleting(false);
    }
  };

  const handleAddPointItem = async () => {
    if (!newItemName) return;
    try {
      const points = itemsSubTab === 'bonus' ? Math.abs(newItemPoints) : -Math.abs(newItemPoints);
      const response = await api.post('/point-items', {
        name: newItemName,
        points: points,
        icon: newItemIcon, // Use selected icon
        classId: currentClass.id
      });
      setPointItems([response.data, ...pointItems]);
      setNewItemName('');
      setNewItemPoints(1);
      setNewItemIcon('Star'); // Reset to default
    } catch (error) {
      console.error('Failed to add point item', error);
    }
  };

  const handleUpdatePointItem = async () => {
    if (!editingItem || !editingItem.name) return;
    try {
      const points = itemsSubTab === 'bonus' ? Math.abs(editingItem.points) : -Math.abs(editingItem.points);
      const response = await api.put(`/point-items/${editingItem.id}`, {
          name: editingItem.name,
          points: points,
          icon: editingItem.icon,
          classId: currentClass.id
        });
      
      setPointItems(pointItems.map(item => item.id === editingItem.id ? response.data : item));
      setEditingItem(null);
      showToast(t('settings.update.success'), 'success');
    } catch (error) {
      console.error('Failed to update point item', error);
      showToast(t('settings.update.failed'), 'error');
    }
  };

  const handleDeletePointItem = async (id: number) => {
    try {
      await api.delete(`/point-items/${id}?classId=${currentClass.id}`);
      setPointItems(pointItems.filter(item => item.id !== id));
    } catch (error) {
      console.error('Failed to delete point item', error);
    }
  };

  const handleAddStudent = async () => {
    if (!newStudentName || !currentClass) return;
    
    if (students.some(s => s.name === newStudentName.trim())) {
      showToast(t('settings.students.alreadyExists'), 'error');
      return;
    }

    try {
      await api.post('/students', {
        name: newStudentName,
        studentNumber: '',
        classId: currentClass.id
      });
      setNewStudentName('');
      fetchStudents();
      onUpdate?.();
    } catch (error) {
      console.error('Failed to add student', error);
    }
  };


  const handleUpdateStudent = async () => {
    if (!editingStudent || !editingStudent.name) return;
    try {
      await api.put(`/students/${editingStudent.id}`, {
        name: editingStudent.name,
        studentNumber: editingStudent.studentNumber
      });
      
      setStudents(students.map(s => s.id === editingStudent.id ? editingStudent : s));
      setEditingStudent(null);
      onUpdate?.();
      showToast(t('settings.update.success'), 'success');
    } catch (error) {
      console.error('Failed to update student', error);
      showToast(t('settings.update.failed'), 'error');
    }
  };

  const handleDeleteStudent = async (id: number) => {
    try {
      await api.delete(`/students/${id}`);
      setStudents(students.filter(s => s.id !== id));
      onUpdate?.();
      setDeleteConfirmation(null);
      showToast(t('settings.delete.success'), 'success');
    } catch (error) {
      console.error('Failed to delete student', error);
      showToast(t('settings.delete.failed'), 'error');
    }
  };

  const handleAssignPets = async () => {
    if (!currentClass) return;
    try {
      await api.post('/students/assign-pets', { 
        classId: currentClass.id,
        preferUnique: true
      });
      fetchStudents();
      onUpdate?.();
      setAssignConfirmation(false);
      showToast(t('settings.pets.assignSuccess'), 'success');
    } catch (error) {
      console.error('Failed to assign pets', error);
      showToast(t('settings.pets.assignFailed'), 'error');
    }
  };

  const handleResetClass = async () => {
    if (!currentClass || !resetClassPassword) return;
    setIsResettingClass(true);
    setResetClassError('');
    try {
      await api.post(`/classes/${currentClass.id}/reset-progress`, {
        password: resetClassPassword
      });
      fetchStudents();
      fetchSettings();
      fetchPointItems();
      onUpdate?.();
      onGrowthUpdate?.();
      setResetClassConfirmation(false);
      setResetClassPassword('');
      showToast(t('settings.pets.reset.success'), 'success');
    } catch (error) {
      console.error('Failed to reset class', error);
      const err = error as { response?: { status?: number } };
      if (err.response?.status === 403) {
         setResetClassError(t('settings.class.deleteConfirm.passwordError'));
      } else {
         setResetClassError(t('settings.pets.reset.failed'));
      }
    } finally {
      setIsResettingClass(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setPointItems((items) => {
        const currentTabItems = items.filter(item => itemsSubTab === 'bonus' ? item.points > 0 : item.points < 0);
        const oldIndex = currentTabItems.findIndex((item) => item.id === active.id);
        const newIndex = currentTabItems.findIndex((item) => item.id === over?.id);
        
        if (oldIndex === -1 || newIndex === -1) return items;

        const newSortedSubList = arrayMove(currentTabItems, oldIndex, newIndex);
        
        // Merge back into main list
        let subIndex = 0;
        const newItems = items.map(item => {
           const isBonus = item.points > 0;
           const isCurrentTab = itemsSubTab === 'bonus' ? isBonus : !isBonus;
           if (isCurrentTab) {
              return newSortedSubList[subIndex++];
           }
           return item;
        });

        // Save order to backend
        const reorderedItems = newItems.map((item, index) => ({
             id: item.id,
             order: index
        }));
        
        api.post('/point-items/reorder', reorderedItems).catch(err => console.error(err));
        
        return newItems;
      });
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'students', label: t('settings.tabs.students'), icon: Users },
    { id: 'checkin', label: t('settings.tabs.checkin'), icon: CalendarCheck },
    { id: 'items', label: t('settings.tabs.items'), icon: LayoutGrid },
    { id: 'growth', label: t('settings.tabs.growth'), icon: Fish },
    { id: 'petLife', label: t('settings.petLife.title'), icon: Clock },
    { id: 'account', label: t('settings.tabs.account'), icon: Settings },
  ];

  return (
    <>
    {toast && (
      <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
    )}
    <div className="fixed inset-0 bg-black/50 flex items-start md:items-center justify-center z-[100] backdrop-blur-sm p-2 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[calc(100vh-1rem)] md:h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-3 sm:p-5 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-pink-50 flex items-center justify-center">
              <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">{t('common.systemSettings')}</h2>
          </div>
          <div className="flex items-center gap-2">
            {/* 桌面快捷方式按钮已隐藏 */}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0">
          {/* Mobile: iOS segmented control */}
          <div className="md:hidden border-b border-gray-100 bg-white px-3 pt-2 pb-3">
            <div className="flex gap-1 bg-pink-50/70 rounded-2xl p-1 overflow-x-auto scrollbar-none">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-white text-pink-600 shadow-sm shadow-pink-100/60'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              <button
                onClick={handleLogout}
                className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-bold text-red-500/80 hover:text-red-600 whitespace-nowrap transition-colors"
              >
                {t('common.logoutShort')}
              </button>
            </div>
          </div>

          {/* Desktop: sidebar */}
          <div className="hidden md:flex w-64 bg-gray-50/50 border-r border-gray-100 p-4 flex-col overflow-y-auto gap-2 shrink-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-pink-50 text-pink-600 ring-1 ring-pink-100'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-pink-600' : 'text-gray-400'}`} />
                {tab.label}
              </button>
            ))}

            <div className="pt-4 mt-4 border-t border-gray-200">
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-red-500 hover:bg-red-50 transition-all whitespace-nowrap">
                <RotateCcw className="w-5 h-5" />
                {t('common.logout')}
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-white min-h-0">
            <div className="p-4 sm:p-6 md:p-8 max-w-3xl mx-auto">
          
              {/* parentShare tab removed — single family mode */}
              {false && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="bg-pink-50/50 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 border border-pink-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-pink-100 shrink-0">
                        <Link2 className="w-5 h-5 text-pink-500" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-800 truncate">{t('settings.parentShare.title')}</h3>
                        <div className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
                          {t('settings.parentShare.subtitle')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-white p-3 sm:p-4 md:p-6 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div
                          className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${parentShareEnabled ? 'bg-pink-100 text-pink-600' : 'bg-gray-100 text-gray-500'}`}
                        >
                          <Link2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-gray-800 truncate text-sm sm:text-base">
                            {parentShareEnabled ? t('settings.parentShare.enabled') : t('settings.parentShare.disabled')}
                          </div>
                          <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">
                            {t('settings.parentShare.disabledHint')}
                          </div>
                        </div>
                      </div>
                      <label
                        className={`relative inline-flex items-center cursor-pointer ml-2 shrink-0 ${!currentClass ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={parentShareEnabled}
                          disabled={!currentClass}
                          onChange={(e) => handleToggleParentShareEnabled(e.target.checked)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
                      </label>
                    </div>

                    <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                      <div className="grid grid-cols-3 sm:flex sm:flex-row gap-2">
                        <button
                          onClick={handleGenerateParentShareLink}
                          disabled={!currentClass || isParentShareLinkLoading}
                          className="col-span-3 sm:w-auto px-3 sm:px-4 py-2 bg-pink-500 text-white rounded-lg font-bold text-xs sm:text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 sm:gap-2 shadow-sm"
                        >
                          <Link2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          {isParentShareLinkLoading ? t('settings.parentShare.processing') : t('settings.parentShare.generateLink')}
                        </button>
                        <button
                          onClick={async () => {
                            if (!parentShareLink) return;
                            const ok = await copyToClipboard(parentShareLink);
                            showToast(
                              ok ? t('settings.parentShare.toast.copied') : t('settings.parentShare.toast.copyFailed'),
                              ok ? 'success' : 'error'
                            );
                          }}
                          disabled={!parentShareLink}
                          className="col-span-2 sm:w-auto px-3 sm:px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 font-bold text-xs sm:text-sm shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 sm:gap-2"
                        >
                          <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          {t('settings.parentShare.copyLink')}
                        </button>
                        <button
                          onClick={handleRotateParentShareLink}
                          disabled={!currentClass || isParentShareLinkLoading}
                          className="col-span-1 sm:w-auto px-2 sm:px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 font-bold text-xs sm:text-sm shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('settings.parentShare.rotateLink')}
                        </button>
                      </div>

                      <input
                        type="text"
                        value={parentShareLink}
                        readOnly
                        placeholder={t('settings.parentShare.linkPlaceholder')}
                        onFocus={(e) => e.target.select()}
                        className="w-full border border-gray-200 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition-all bg-gray-50 focus:bg-white"
                      />
                      <div className="text-[11px] sm:text-xs text-gray-500 leading-relaxed">
                        {t('settings.parentShare.tip')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Section: Account & System */}
              {activeTab === 'account' && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="bg-blue-50/50 rounded-2xl p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8 border border-blue-100">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-pink-400 flex items-center justify-center text-white text-lg sm:text-xl md:text-2xl font-bold shadow-md shrink-0">
                          {avatarLetter}
                        </div>
                        <div>
                          <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800">
                            {displayUsername}
                          </h3>
                          <div className="mt-1">
                            <span
                              className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 border whitespace-nowrap ${validUntil
                                ? 'bg-amber-100 text-amber-700 border-amber-200'
                                : 'bg-green-100 text-green-600 border-green-200'
                                }`}
                            >
                              {formatValidUntil(validUntil)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 w-full sm:w-auto">
                        <button
                          onClick={() => setIsPasswordModalOpen(true)}
                          className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 font-medium text-xs sm:text-sm shadow-sm transition-colors whitespace-nowrap"
                        >
                          {t('settings.account.changePassword')}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 sm:space-y-6">
                    {/* <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                        <Settings className="w-4 h-4" /> 系统名称 (全局)
                      </label>
                      <input
                        type="text"
                        value={systemName}
                        onChange={(e) => setSystemName(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition-all bg-gray-50 focus:bg-white"
                      />
                    </div> */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2">
                        <User className="w-4 h-4" /> {t('settings.account.currentClassName')}
                      </label>
                      <input
                        type="text"
                        value={className}
                        onChange={(e) => setClassName(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition-all bg-gray-50 focus:bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-2">
                        <Settings className="w-4 h-4" /> {t('settings.language.label')}
                      </label>
                      <select
                        value={activeLanguage}
                        onChange={(e) => i18n.changeLanguage(normalizeLanguage(e.target.value))}
                        className="w-full border border-gray-200 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base focus:ring-2 focus:ring-pink-200 focus:border-pink-400 outline-none transition-all bg-gray-50 focus:bg-white"
                      >
                        <option value="zh-CN">{t('settings.language.zhCN')}</option>
                        <option value="zh-TW">{t('settings.language.zhTW')}</option>
                        <option value="en-US">{t('settings.language.enUS')}</option>
                      </select>
                    </div>

                    {/* Class list removed — single family mode */}
                    
                    <div className="hidden pt-4 sm:pt-6 border-t border-gray-100">
                      <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
                        <Monitor className="w-5 h-5 text-gray-500" /> {t('settings.account.desktop.title')}
                      </h3>
                      <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-100">
                        <p className="text-xs sm:text-sm text-gray-500 mb-3">{t('settings.account.desktop.desc')}</p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={() => {
                              const url = 'https://pet.wxmini.club/';
                              const content = '[InternetShortcut]\r\nURL=' + url + '\r\nIconIndex=0\r\nIconFile=https://pet-cdn.wxmini.club/unicorn/1.png\r\n';
                              const blob = new Blob([content], { type: 'application/internet-shortcut' });
                              const a = document.createElement('a');
                              a.href = URL.createObjectURL(blob);
                              a.download = t('settings.account.desktop.filename') + '.url';
                              a.click();
                              URL.revokeObjectURL(a.href);
                            }}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold text-xs sm:text-sm hover:bg-indigo-700 transition-colors shadow-sm"
                          >
                            <Download className="w-4 h-4" />
                            {t('settings.account.desktop.download')}
                          </button>
                          <p className="text-[11px] sm:text-xs text-gray-400 self-center">{t('settings.account.desktop.hint')}</p>
                        </div>
                      </div>
                    </div>

                    {/* <div className="pt-6 border-t border-gray-100">
                      <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <Palette className="w-5 h-5 text-gray-500" /> 界面主题
                      </h3>
                      <div className="flex flex-wrap gap-4">
                        {themes.map((theme) => (
                          <button
                            key={theme.id}
                            onClick={() => setThemeColor(theme.id)}
                            className={`group relative flex flex-col items-center gap-2 p-2 rounded-xl transition-all ${
                              themeColor === theme.id ? 'bg-gray-100' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-full ${theme.color} shadow-sm ${
                              themeColor === theme.id ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                            }`}></div>
                            <span className="text-xs font-medium text-gray-600">{theme.name}</span>
                          </button>
                        ))}
                      </div>
                    </div> */}
                  </div>
                </div>
              )}

              {/* Section: Growth Config */}
              {activeTab === 'growth' && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="space-y-4 sm:space-y-6">
                    <div className="rounded-2xl border border-gray-100 bg-white p-3 sm:p-5 md:p-6 shadow-sm">
                      <div className="flex flex-col items-start sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
                        <div>
                          <h3 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
                            <Fish className="w-5 h-5 text-gray-500" /> {t('settings.growth.title')}
                          </h3>
                          <p className="text-gray-400 text-xs sm:text-sm mt-1">{t('settings.growth.subtitle')}</p>
                        </div>
                        {/* Import button removed — single family mode */}
                      </div>

                      <div className="grid grid-cols-5 gap-1.5 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
                        {growthConfig.map((value, index) => (
                          <div key={index} className="flex flex-col items-center p-1.5 sm:p-2 md:p-3 border border-gray-100 rounded-lg sm:rounded-xl bg-gray-50">
                            <span className="text-[10px] sm:text-xs text-gray-400 mb-1 sm:mb-1.5 font-medium">Lv.{index + 1}</span>
                            <input
                              type="number"
                              max="9999"
                              value={index === 0 ? 0 : value}
                              disabled={index === 0}
                              onChange={(e) => {
                                const newConfig = [...growthConfig];
                                newConfig[index] = Number(e.target.value);
                                setGrowthConfig(newConfig);
                              }}
                              className={`w-full text-center border rounded-md sm:rounded-lg py-1 sm:py-1.5 text-xs sm:text-sm md:text-base font-bold ${
                                index === 9
                                  ? 'border-orange-200 bg-orange-50 text-orange-500'
                                  : index === 0
                                    ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'border-gray-200 bg-white text-gray-700'
                              } focus:ring-2 focus:ring-pink-200 outline-none`}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="bg-blue-50 text-blue-600 text-xs sm:text-sm p-3 sm:p-4 rounded-xl flex items-start gap-2 border border-blue-100">
                        <div className="mt-0.5 shrink-0">💡</div>
                        <p>{t('settings.growth.tip')}</p>
                      </div>

                    </div>
                  </div>
                </div>
              )}

              {/* Section: Pet Life Config */}
              {activeTab === 'petLife' && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="space-y-4 sm:space-y-6">
                    <div className="rounded-2xl border border-gray-100 bg-white p-3 sm:p-4 md:p-6 shadow-sm">
                      <div className="flex flex-col gap-3 mb-2">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 sm:mb-2">
                              <Clock className="w-5 h-5 text-amber-500 shrink-0" />
                              <h4 className="text-base sm:text-lg font-bold text-gray-800">{t('settings.petLife.title')}</h4>
                            </div>
                            <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">{t('settings.petLife.subtitle')}</p>
                          </div>

                          {/* Import button removed — single family mode */}
                          {/* <button
                            onClick={handleResetPetLifeConfig}
                            className="text-amber-600 text-xs sm:text-sm font-bold hover:text-amber-700 flex flex-col sm:flex-row items-center gap-1 bg-amber-50 px-3 py-2 sm:py-1.5 rounded-lg border border-amber-100 whitespace-nowrap shrink-0 transition-colors"
                          >
                            <RotateCcw className="w-4 h-4" />
                            <span>{t('settings.petLife.reset')}</span>
                          </button> */}
                        </div>
                      </div>

                      <div className="mt-3 sm:mt-6 mb-3 sm:mb-6 flex items-center justify-between bg-gray-50/50 p-3 sm:p-4 rounded-xl border border-gray-100 gap-3">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shrink-0 ${classSettings.isAttenuationEnabled ? 'bg-amber-100 text-amber-600' : 'bg-gray-200 text-gray-500'}`}>
                            <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-gray-800 truncate text-sm sm:text-base">
                              {classSettings.isAttenuationEnabled ? t('settings.petLife.enabled') : t('settings.petLife.disabled')}
                            </div>
                            <div className="text-[11px] sm:text-xs text-gray-500 mt-0.5 leading-tight line-clamp-2">
                              {classSettings.isAttenuationEnabled ? t('settings.petLife.enabledDesc') : t('settings.petLife.disabledDesc')}
                            </div>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-2 shrink-0">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={classSettings.isAttenuationEnabled ?? false}
                            disabled={savingClassSettingKey === 'isAttenuationEnabled'}
                            onChange={(e) => handleToggleClassSetting('isAttenuationEnabled', e.target.checked)}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
                        </label>
                      </div>

                        <>
                          <div className="grid grid-cols-4 gap-1.5 sm:gap-2 md:gap-3 mb-3 sm:mb-6">
                            {[
                              { key: 'hungryAfterDays', labelKey: 'hungryLabel', label: t('settings.petLife.hungryAfterDays'), defaultLabel: t('petLife.states.hungry'), accent: 'border-amber-200 bg-amber-50 text-amber-600 focus:ring-amber-200' },
                              { key: 'weakAfterDays', labelKey: 'weakLabel', label: t('settings.petLife.weakAfterDays'), defaultLabel: t('petLife.states.weak'), accent: 'border-slate-200 bg-slate-50 text-slate-600 focus:ring-slate-200' },
                              { key: 'dyingAfterDays', labelKey: 'dyingLabel', label: t('settings.petLife.dyingAfterDays'), defaultLabel: t('petLife.states.dying'), accent: 'border-orange-200 bg-orange-50 text-orange-600 focus:ring-orange-200' },
                              { key: 'sleepAfterDays', labelKey: 'deadLabel', label: t('settings.petLife.sleepAfterDays'), defaultLabel: t('petLife.states.dead'), accent: 'border-violet-200 bg-violet-50 text-violet-600 focus:ring-violet-200' },
                            ].map((item) => (
                              <div key={item.key} className="flex flex-col p-1.5 sm:p-2.5 md:p-4 border border-gray-100 rounded-lg sm:rounded-xl md:rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-center mb-1.5 sm:mb-2 md:mb-3">
                                  <span className="text-[10px] sm:text-xs md:text-sm text-gray-500 font-bold bg-gray-50 px-1.5 sm:px-2.5 md:px-3 py-0.5 sm:py-1 rounded-full text-center leading-tight">{item.label}</span>
                                </div>
                                <div className="relative mb-1.5 sm:mb-2">
                                  <input
                                    type="number"
                                    min={1}
                                    max={9999}
                                    value={petLifeConfig[item.key as 'hungryAfterDays' | 'weakAfterDays' | 'dyingAfterDays' | 'sleepAfterDays']}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      setPetLifeConfig((prev) => ({
                                        ...prev,
                                        [item.key]: raw === '' ? '' : Number(raw),
                                      }));
                                      setPetLifeConfigError('');
                                    }}
                                    className={`w-full text-center border-2 rounded-lg sm:rounded-xl py-1.5 sm:py-2 md:py-2.5 text-sm sm:text-base md:text-lg font-black ${item.accent} focus:ring-4 outline-none transition-all`}
                                  />
                                  <span className="absolute right-1.5 sm:right-2.5 md:right-3 top-1/2 -translate-y-1/2 text-[10px] sm:text-xs md:text-sm font-medium opacity-50">{t('settings.petLife.daysUnit', '天')}</span>
                                </div>
                                <input
                                  type="text"
                                  maxLength={5}
                                  placeholder={item.defaultLabel}
                                  value={petLifeConfig[item.labelKey as keyof PetLifeStateConfig] as string || ''}
                                  onChange={(e) => {
                                    setPetLifeConfig((prev) => ({
                                      ...prev,
                                      [item.labelKey]: e.target.value,
                                    }));
                                  }}
                                  className="w-full text-center border border-gray-200 rounded-lg py-1 text-[10px] sm:text-xs text-gray-500 placeholder:text-gray-300 outline-none focus:border-gray-400 transition-colors"
                                />
                              </div>
                            ))}
                          </div>

                          {/* Skip Weekends */}
                          <label className="flex items-center gap-2.5 mb-3 sm:mb-5 px-1 cursor-pointer select-none group">
                            <input
                              type="checkbox"
                              checked={!!petLifeConfig.skipWeekends}
                              onChange={(e) => setPetLifeConfig(prev => ({ ...prev, skipWeekends: e.target.checked }))}
                              className="w-4 h-4 rounded border-gray-300 text-indigo-500 focus:ring-indigo-400 cursor-pointer"
                            />
                            <span className="text-xs sm:text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                              {t('settings.petLife.skipWeekends', '周末（周六、周日）不计入衰减天数')}
                            </span>
                          </label>

                          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-2.5 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl border border-gray-200/60 mb-3 sm:mb-6">
                            <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-3 md:mb-4 flex items-center gap-2">
                              <span className="text-base sm:text-lg">🖼️</span> {t('settings.petLife.preview.title')}
                            </h4>
                            <div className="grid grid-cols-4 gap-1.5 sm:gap-3 md:gap-4">
                              {[
                                { img: 'https://pet-cdn.wxmini.club/status/2.png', label: t('settings.petLife.preview.hungry'), color: 'text-amber-600 bg-amber-50' },
                                { img: 'https://pet-cdn.wxmini.club/status/3.png', label: t('settings.petLife.preview.weak'), color: 'text-slate-600 bg-slate-50' },
                                { img: 'https://pet-cdn.wxmini.club/status/4.png', label: t('settings.petLife.preview.dying'), color: 'text-orange-600 bg-orange-50' },
                                { img: 'https://pet-cdn.wxmini.club/status/5.png', label: t('settings.petLife.preview.dead'), color: 'text-violet-600 bg-violet-50' },
                              ].map((item, index) => (
                                <div key={index} className="flex flex-col justify-center items-center py-2 sm:py-3 md:py-4 bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100/80 group">
                                  <span className={`text-[9px] sm:text-[11px] md:text-xs font-bold px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full mb-1.5 sm:mb-2 md:mb-3 ${item.color}`}>{item.label}</span>
                                  <div className="relative w-10 h-10 sm:w-16 sm:h-16 md:w-24 md:h-24">
                                    <div className="absolute inset-0 bg-gray-50 rounded-full scale-110 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                    <img
                                      src={item.img}
                                      alt={item.label}
                                      className="relative z-10 w-full h-full object-contain drop-shadow-md group-hover:scale-110 group-hover:drop-shadow-xl transition-all duration-300 cursor-pointer"
                                      onClick={() => setPreviewImage(item.img)}
                                      title={t('settings.petLife.preview.zoomHint')}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {petLifeConfigError && (
                            <div className="mb-4 bg-red-50 text-red-600 text-sm p-3 rounded-xl border border-red-100">
                              {petLifeConfigError}
                            </div>
                          )}

                          <div className="bg-amber-50 text-amber-700 text-[11px] sm:text-xs md:text-sm p-2.5 sm:p-3 md:p-4 rounded-xl border border-amber-100">
                            <div className="flex items-start gap-2">
                              <div className="mt-0.5 shrink-0">💡</div>
                              <div className="space-y-1 sm:space-y-1.5">
                                <p className="font-medium leading-relaxed">{t('settings.petLife.tip')}</p>
                                <p className="opacity-90 leading-relaxed">{t('settings.petLife.rulePreview', {
                                  hungryAfterDays: petLifeConfig.hungryAfterDays,
                                  weakAfterDays: petLifeConfig.weakAfterDays,
                                  dyingAfterDays: petLifeConfig.dyingAfterDays,
                                  sleepAfterDays: petLifeConfig.sleepAfterDays,
                                })}</p>
                              </div>
                            </div>
                          </div>
                        </>
                      
                    </div>
                  </div>
                </div>
              )}

              {/* Section: Point Items */}
              {activeTab === 'items' && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="top-0 z-10 bg-white/95 backdrop-blur-sm -mt-4 md:-mt-8 -mx-4 md:-mx-8 px-4 md:px-8 pt-4 md:pt-8 pb-4 shadow-sm mb-4 sm:mb-6 border-b border-gray-100">
                    <div className="flex flex-col gap-2 mb-4 sm:mb-6">
                      <div className="flex flex-col items-start md:flex-row md:items-center md:justify-between gap-3">
                        <h3 className="text-lg font-bold text-gray-800">{t('settings.items.title')}</h3>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <button
                            onClick={() => setIsSystemTemplateModalOpen(true)}
                            className="text-emerald-600 text-xs sm:text-sm font-bold hover:text-emerald-700 flex items-center gap-1 bg-emerald-50 px-2.5 sm:px-3 py-1.5 rounded-lg border border-emerald-100 whitespace-nowrap flex-1 sm:flex-none justify-center"
                          >
                            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> {t('settings.items.importSystemTemplate')}
                          </button>
                          {/* Import button removed — single family mode */}
                        </div>
                      </div>
                      <p className="text-gray-400 text-xs sm:text-sm">
                        {t('settings.items.subtitle', { count: pointItems.length })}
                        <span className="text-orange-400 ml-1">
                          {t('settings.items.hint', { maxPoints: growthConfig[growthConfig.length - 1] })}
                        </span>
                      </p>
                    </div>

                    {/* Sub Tabs */}
                    <div className="flex p-1 bg-gray-100 rounded-xl mb-4 sm:mb-6 w-fit">
                      <button
                        onClick={() => { setItemsSubTab('bonus'); setEditingItem(null); }}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${itemsSubTab === 'bonus' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        {t('settings.items.subTabs.bonus')}
                      </button>
                      <button
                        onClick={() => { setItemsSubTab('deduction'); setEditingItem(null); }}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${itemsSubTab === 'deduction' ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        {t('settings.items.subTabs.deduction')}
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 bg-gray-50 p-2.5 sm:p-3 rounded-xl border border-dashed border-gray-300">
                      <button
                        onClick={() => {
                          setPickerTarget('new');
                          setShowIconPicker(true);
                        }}
                        className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-pink-500 hover:border-pink-300 shadow-sm shrink-0 transition-all"
                      >
                        {React.createElement(ICON_MAP[newItemIcon] || Star, { className: "w-5 h-5" })}
                      </button>
                      <input
                        type="text"
                        placeholder={itemsSubTab === 'bonus' ? t('settings.items.newItemPlaceholder.bonus') : t('settings.items.newItemPlaceholder.deduction')}
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        className="flex-1 min-w-[120px] bg-white border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-pink-400 transition-colors text-sm"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddPointItem()}
                      />
                      <div className="flex items-center bg-white border border-gray-200 rounded-lg px-1 shrink-0">
                        <button onClick={() => setNewItemPoints(p => Math.max(1, p - 1))} className="p-1.5 text-gray-400 hover:text-pink-500">-</button>
                        <input
                          type="number"
                          max="9999"
                          value={newItemPoints}
                          onChange={(e) => setNewItemPoints(Math.min(9999, Math.max(1, Number(e.target.value))))}
                          className={`w-14 text-center outline-none font-bold text-sm ${itemsSubTab === 'bonus' ? 'text-green-600' : 'text-red-600'}`}
                        />
                        <button onClick={() => setNewItemPoints(p => p + 1)} className="p-1.5 text-gray-400 hover:text-pink-500">+</button>
                      </div>
                      <button
                        onClick={handleAddPointItem}
                        className={`px-4 py-1.5 text-white rounded-lg font-bold text-sm transition-colors flex items-center justify-center gap-1.5 shadow-sm whitespace-nowrap shrink-0 ${itemsSubTab === 'bonus' ? 'bg-green-500 hover:bg-green-600 shadow-green-200' : 'bg-red-500 hover:bg-red-600 shadow-red-200'}`}
                      >
                        {t('common.add')}
                      </button>
                    </div>
                  </div>

                  <DndContext 
                    sensors={sensors} 
                    collisionDetection={closestCenter} 
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext 
                      items={pointItems
                        .filter(item => itemsSubTab === 'bonus' ? item.points > 0 : item.points < 0)
                        .map(i => i.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                        {pointItems
                          .filter(item => itemsSubTab === 'bonus' ? item.points > 0 : item.points < 0)
                          .map((item, index) => (
                            <SortableItem key={item.id} id={item.id}>
                              <div className="flex items-center justify-between px-3 py-2 sm:px-4 sm:py-2.5 bg-white border border-gray-100 rounded-xl hover:shadow-md transition-all group gap-2 sm:gap-3 w-full">
                                {editingItem?.id === item.id ? (
                          <div className="flex flex-wrap items-center gap-2 w-full animate-in fade-in duration-200">
                             <button
                                onClick={() => {
                                  setPickerTarget('edit');
                                  setShowIconPicker(true);
                                }}
                                className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-500 hover:text-pink-500 hover:border-pink-300 shadow-sm shrink-0 transition-all"
                              >
                                {React.createElement(ICON_MAP[editingItem.icon] || Star, { className: "w-4 h-4" })}
                              </button>
                             <input
                                type="text"
                                value={editingItem.name}
                                onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                                className={`flex-1 min-w-[100px] bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 text-sm outline-none transition-all ${itemsSubTab === 'bonus' ? 'focus:border-green-500 focus:ring-2 focus:ring-green-100' : 'focus:border-red-500 focus:ring-2 focus:ring-red-100'}`}
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && handleUpdatePointItem()}
                              />
                            <div className="flex items-center gap-1.5 shrink-0">
                              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-0.5">
                                <button
                                  onClick={() => setEditingItem(prev => {
                                    if (!prev) return null;
                                    const isNegative = prev.points < 0;
                                    const currentAbs = Math.abs(prev.points);
                                    const newAbs = Math.max(1, currentAbs - 1);
                                    return { ...prev, points: isNegative ? -newAbs : newAbs };
                                  })}
                                  className="p-1 text-gray-400 hover:text-pink-500 text-sm"
                                >-</button>
                                <input
                                  type="number"
                                  max="9999"
                                  value={Math.abs(editingItem.points)}
                                  onChange={(e) => setEditingItem(prev => {
                                    if (!prev) return null;
                                    const isNegative = prev.points < 0;
                                    const newAbs = Math.min(9999, Math.max(1, Number(e.target.value)));
                                    return { ...prev, points: isNegative ? -newAbs : newAbs };
                                  })}
                                  className={`w-14 text-center outline-none font-bold text-sm bg-transparent ${itemsSubTab === 'bonus' ? 'text-green-600' : 'text-red-600'}`}
                                />
                                <button
                                  onClick={() => setEditingItem(prev => {
                                    if (!prev) return null;
                                    const isNegative = prev.points < 0;
                                    const currentAbs = Math.abs(prev.points);
                                    const newAbs = currentAbs + 1;
                                    return { ...prev, points: isNegative ? -newAbs : newAbs };
                                  })}
                                  className="p-1 text-gray-400 hover:text-pink-500 text-sm"
                                >+</button>
                              </div>
                              <button onClick={handleUpdatePointItem} className="p-1 text-green-500 hover:bg-green-50 rounded-lg">
                                <Check className="w-4 h-4" />
                              </button>
                              <button onClick={() => setEditingItem(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded-lg">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 sm:gap-3 overflow-hidden flex-1 min-w-0">
                              <span className="text-gray-300 font-mono w-4 text-xs shrink-0">{index + 1}</span>
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.points > 0 ? 'bg-green-50 text-green-500' : 'bg-red-50 text-red-500'}`}>
                                {React.createElement(ICON_MAP[item.icon] || (item.points > 0 ? Plus : Star), { className: "w-4 h-4 sm:w-5 sm:h-5" })}
                              </div>
                              <span className="font-bold text-gray-700 text-sm truncate min-w-0">{item.name}</span>
                            </div>
                            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${item.points > 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                                {t('settings.items.points', { points: item.points > 0 ? `+${item.points}` : `${item.points}` })}
                              </span>
                              <button onClick={() => setEditingItem(item)} className="text-gray-300 hover:text-blue-500 transition-colors p-1 rounded-full hover:bg-blue-50">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeletePointItem(item.id)} className="text-gray-300 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </>
                        )}
                              </div>
                            </SortableItem>
                          ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Section: Check-in Tasks */}
              {activeTab === 'checkin' && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="top-0 z-10 bg-white/95 backdrop-blur-sm -mt-4 md:-mt-8 -mx-4 md:-mx-8 px-4 md:px-8 pt-4 md:pt-8 pb-4 shadow-sm mb-4 sm:mb-6 border-b border-gray-100">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center shrink-0">
                            <CalendarCheck className="w-5 h-5 text-sky-500" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-lg font-bold text-gray-800">{t('checkin.taskManagement')}</h3>
                            <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                              {t('checkin.taskManagementSubtitle', { count: checkinTasks.length })}
                            </p>
                          </div>
                        </div>
                        {!checkinTaskForm && (
                          <button
                            onClick={openAddCheckinTaskForm}
                            className="px-3 sm:px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-xl font-bold text-xs sm:text-sm transition-colors flex items-center gap-1.5 shadow-sm shadow-sky-200 shrink-0"
                          >
                            <Plus className="w-4 h-4" />
                            {t('checkin.addTask')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Inline form (add or edit) */}
                  {checkinTaskForm && (
                    <div className="bg-sky-50/50 border border-sky-100 rounded-2xl p-4 sm:p-5 mb-4 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-white border border-sky-200 flex items-center justify-center text-sky-500 shrink-0">
                          <CalendarCheck className="w-4 h-4" />
                        </div>
                        <h4 className="text-sm font-bold text-gray-800">
                          {checkinTaskForm.id ? t('checkin.form.editTitle') : t('checkin.form.addTitle')}
                        </h4>
                      </div>

                      {/* Name */}
                      <div className="mb-3">
                        <label className="block text-xs font-bold text-gray-600 mb-1.5">{t('checkin.form.name')}</label>
                        <input
                          type="text"
                          value={checkinTaskForm.name}
                          onChange={(e) => setCheckinTaskForm(prev => prev ? { ...prev, name: e.target.value } : prev)}
                          placeholder={t('checkin.taskNamePlaceholder')}
                          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition-all"
                          autoFocus
                        />
                      </div>

                      {/* Points */}
                      <div className="mb-3">
                        <label className="block text-xs font-bold text-gray-600 mb-1.5">{t('checkin.form.points')}</label>
                        <div className="flex items-center bg-white border border-gray-200 rounded-lg px-1 w-fit">
                          <button
                            type="button"
                            onClick={() => setCheckinTaskForm(prev => prev ? { ...prev, points: Math.max(1, prev.points - 1) } : prev)}
                            className="p-1.5 text-gray-400 hover:text-sky-500"
                          >-</button>
                          <input
                            type="number"
                            min={1}
                            max={9999}
                            value={checkinTaskForm.points}
                            onChange={(e) => setCheckinTaskForm(prev => prev ? { ...prev, points: Math.min(9999, Math.max(1, Number(e.target.value) || 1)) } : prev)}
                            className="w-16 text-center outline-none font-bold text-sm text-sky-600 bg-transparent"
                          />
                          <button
                            type="button"
                            onClick={() => setCheckinTaskForm(prev => prev ? { ...prev, points: Math.min(9999, prev.points + 1) } : prev)}
                            className="p-1.5 text-gray-400 hover:text-sky-500"
                          >+</button>
                        </div>
                      </div>

                      {/* Date range */}
                      <div className="mb-3">
                        <label className="block text-xs font-bold text-gray-600 mb-1.5">{t('checkin.form.dateRange')}</label>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="date"
                            value={checkinTaskForm.startDate}
                            onChange={(e) => setCheckinTaskForm(prev => prev ? { ...prev, startDate: e.target.value } : prev)}
                            className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-sky-400 transition-colors"
                          />
                          <span className="text-gray-400 text-sm">~</span>
                          <input
                            type="date"
                            value={checkinTaskForm.endDate}
                            onChange={(e) => setCheckinTaskForm(prev => prev ? { ...prev, endDate: e.target.value } : prev)}
                            className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-sky-400 transition-colors"
                          />
                          {(checkinTaskForm.startDate || checkinTaskForm.endDate) && (
                            <button
                              type="button"
                              onClick={() => setCheckinTaskForm(prev => prev ? { ...prev, startDate: '', endDate: '' } : prev)}
                              className="text-xs text-gray-400 hover:text-sky-500 underline"
                            >
                              {t('checkin.form.clearDates')}
                            </button>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1">{t('checkin.form.dateHint')}</p>
                      </div>

                      {/* Repeat days */}
                      <div className="mb-4">
                        <label className="block text-xs font-bold text-gray-600 mb-1.5">{t('checkin.form.repeat')}</label>
                        {/* Presets */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {([
                            { mask: CHECKIN_REPEAT_ALL, label: t('checkin.form.preset.all') },
                            { mask: CHECKIN_REPEAT_WEEKDAYS, label: t('checkin.form.preset.weekdays') },
                            { mask: CHECKIN_REPEAT_WEEKEND, label: t('checkin.form.preset.weekend') },
                          ]).map(preset => {
                            const active = checkinTaskForm.repeatDays === preset.mask;
                            return (
                              <button
                                key={preset.label}
                                type="button"
                                onClick={() => setCheckinTaskForm(prev => prev ? { ...prev, repeatDays: preset.mask } : prev)}
                                className={`px-3 py-1 rounded-full text-xs font-bold border transition-all ${
                                  active
                                    ? 'bg-sky-500 border-sky-500 text-white shadow-sm shadow-sky-200'
                                    : 'bg-white border-gray-200 text-gray-500 hover:border-sky-300 hover:text-sky-600'
                                }`}
                              >
                                {preset.label}
                              </button>
                            );
                          })}
                        </div>
                        {/* Individual day toggles */}
                        <div className="flex flex-wrap gap-1.5">
                          {CHECKIN_WEEKDAY_DEFS.map(d => {
                            const bitVal = 1 << d.bit;
                            const active = (checkinTaskForm.repeatDays & bitVal) !== 0;
                            return (
                              <button
                                key={d.key}
                                type="button"
                                onClick={() => setCheckinTaskForm(prev => prev ? { ...prev, repeatDays: prev.repeatDays ^ bitVal } : prev)}
                                className={`w-9 h-9 rounded-lg text-xs font-bold transition-all border ${
                                  active
                                    ? 'bg-sky-500 border-sky-500 text-white shadow-sm shadow-sky-200'
                                    : 'bg-white border-gray-200 text-gray-500 hover:border-sky-300 hover:text-sky-600'
                                }`}
                              >
                                {t(`checkin.weekdayShort.${d.key}`)}
                              </button>
                            );
                          })}
                        </div>
                        {checkinTaskForm.repeatDays === 0 && (
                          <p className="text-[11px] text-rose-500 mt-1.5">{t('checkin.form.repeatRequired')}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={closeCheckinTaskForm}
                          disabled={checkinFormSubmitting}
                          className="px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handleSubmitCheckinTaskForm}
                          disabled={
                            checkinFormSubmitting ||
                            !checkinTaskForm.name.trim() ||
                            checkinTaskForm.points < 1 ||
                            checkinTaskForm.repeatDays === 0
                          }
                          className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm shadow-sm shadow-sky-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t('common.save')}
                        </button>
                      </div>
                    </div>
                  )}

                  {checkinTasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                      <CalendarCheck className="w-12 h-12 mb-3 opacity-30" />
                      <p className="text-sm">{t('checkin.noTasks')}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
                      {checkinTasks.map((task, index) => {
                        const repeatMask = task.repeatDays ?? CHECKIN_REPEAT_ALL;
                        const repeatLabel = (() => {
                          if (repeatMask === CHECKIN_REPEAT_ALL) return t('checkin.form.preset.all');
                          if (repeatMask === CHECKIN_REPEAT_WEEKDAYS) return t('checkin.form.preset.weekdays');
                          if (repeatMask === CHECKIN_REPEAT_WEEKEND) return t('checkin.form.preset.weekend');
                          return CHECKIN_WEEKDAY_DEFS
                            .filter(d => (repeatMask & (1 << d.bit)) !== 0)
                            .map(d => t(`checkin.weekdayShort.${d.key}`))
                            .join(' ');
                        })();
                        const dateLabel =
                          task.startDate || task.endDate
                            ? `${task.startDate || '…'} ~ ${task.endDate || '…'}`
                            : t('checkin.form.dateNoLimit');
                        return (
                          <div
                            key={task.id}
                            className="flex items-start sm:items-center justify-between px-3 py-2.5 sm:px-4 sm:py-3 bg-white border border-gray-100 rounded-xl hover:shadow-md transition-all gap-2 sm:gap-3 w-full"
                          >
                            <div className="flex items-start sm:items-center gap-2 sm:gap-3 overflow-hidden flex-1 min-w-0">
                              <span className="text-gray-300 font-mono w-4 text-xs shrink-0 mt-0.5 sm:mt-0">{index + 1}</span>
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-sky-50 text-sky-500">
                                <CalendarCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-bold text-gray-700 text-sm truncate">{task.name}</div>
                                <div className="text-[11px] text-gray-400 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                  <span className="inline-flex items-center gap-1">
                                    <span className="text-gray-500 font-bold">{repeatLabel}</span>
                                  </span>
                                  <span className="opacity-60">·</span>
                                  <span>{dateLabel}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                              <span className="px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap bg-sky-50 text-sky-600">
                                +{task.points}
                              </span>
                              <button onClick={() => openEditCheckinTaskForm(task)} className="text-gray-300 hover:text-blue-500 transition-colors p-1 rounded-full hover:bg-blue-50">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button onClick={() => setCheckinDeleteConfirm(task)} className="text-gray-300 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Section: Students */}
              {activeTab === 'students' && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 space-y-6">
                  {/* Section header */}
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-pink-50 flex items-center justify-center text-pink-500 shrink-0">
                      <Users className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-gray-800">{t('settings.students.title')}</h3>
                      <p className="text-gray-400 text-sm mt-0.5">{t('settings.students.subtitle', { count: students.length })}</p>
                    </div>
                  </div>

                  {/* Add child input — 单行嵌入式 add button */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('settings.students.singlePlaceholder')}
                      value={newStudentName}
                      onChange={(e) => setNewStudentName(e.target.value)}
                      className="w-full border border-gray-200 rounded-2xl pl-5 pr-28 py-3.5 outline-none focus:border-pink-300 focus:ring-4 focus:ring-pink-50 bg-white transition-all"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddStudent()}
                    />
                    <button
                      onClick={handleAddStudent}
                      disabled={!newStudentName.trim()}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-1 transition-all bg-gradient-to-r from-[#ff4da6] to-[#ff7a59] text-white shadow-md shadow-pink-100 disabled:from-gray-200 disabled:to-gray-200 disabled:text-gray-400 disabled:shadow-none"
                    >
                      <Plus className="w-4 h-4" /> {t('common.add')}
                    </button>
                  </div>

                  {/* Student cards grid */}
                  {students.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {students.map((student) => {
                        const initial = student.name?.trim()?.charAt(0) || '宝';
                        return (
                          <div key={student.id} className="relative bg-white border border-gray-100 rounded-2xl px-3 py-3 hover:border-pink-200 hover:shadow-md hover:shadow-pink-50 transition-all group">
                            {editingStudent?.id === student.id ? (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  value={editingStudent.name}
                                  onChange={(e) => setEditingStudent({ ...editingStudent, name: e.target.value })}
                                  className="w-0 flex-1 min-w-0 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-100 transition-all text-sm"
                                  autoFocus
                                  onKeyDown={(e) => e.key === 'Enter' && handleUpdateStudent()}
                                />
                                <button onClick={handleUpdateStudent} className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg shrink-0">
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => setEditingStudent(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg shrink-0">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-100 to-orange-100 flex items-center justify-center text-pink-500 font-bold text-sm shrink-0">
                                  {initial}
                                </div>
                                <div className="font-bold text-gray-800 text-sm truncate min-w-0 flex-1">{student.name}</div>
                                <div className="flex items-center gap-0.5 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all">
                                  <button onClick={() => setEditingStudent(student)} className="text-gray-300 hover:text-pink-500 p-1 hover:bg-pink-50 rounded-lg transition-colors">
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => setDeleteConfirmation({ id: student.id, name: student.name })} className="text-gray-300 hover:text-red-500 p-1 hover:bg-red-50 rounded-lg transition-colors">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Assign pets card */}
                  <div className="bg-purple-50/60 rounded-2xl p-4 flex items-center justify-between gap-3 border border-purple-100/80">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-500 shrink-0">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-gray-800 text-sm">{t('settings.pets.assign.title')}</h4>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{t('settings.pets.assign.subtitle')}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setAssignConfirmation(true)}
                      className="px-4 py-2 bg-white text-purple-600 rounded-xl font-bold text-sm hover:bg-purple-100/60 active:bg-purple-100 transition-colors shadow-sm whitespace-nowrap shrink-0"
                    >
                      {t('settings.pets.assign.action')}
                    </button>
                  </div>

                  {/* Danger zone — 重置进度做成底部 text-link，避免视觉过载 */}
                  <div className="pt-4 border-t border-gray-100">
                    <button
                      onClick={() => {
                        setResetClassConfirmation(true);
                        setResetClassPassword('');
                        setResetClassError('');
                      }}
                      className="w-full text-left text-sm text-gray-400 hover:text-red-500 transition-colors flex items-center justify-center gap-1.5 py-2"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      {t('settings.pets.reset.title')}
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Footer — 仅在需要显式保存的 tab 上显示（items/students/checkin 都是即时生效，无需 footer） */}
        {(activeTab === 'account' || activeTab === 'growth' || activeTab === 'petLife') && (
          <div className="px-4 md:px-6 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-gray-100 flex justify-end bg-white z-10">
            <button
              onClick={() => handleSaveSettings()}
              disabled={savingClassSettingKey !== null}
              className="px-6 md:px-8 py-2.5 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-colors flex items-center justify-center gap-2 shadow-lg shadow-gray-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" /> {t('common.saveSettings')}
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Assign Confirmation Modal */}
    {assignConfirmation && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setAssignConfirmation(false)}
        ></div>
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mx-auto mb-4">
              <RotateCcw className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('settings.pets.assignConfirm.title')}</h3>
            <p className="text-gray-500 text-sm mb-6">
              {t('settings.pets.assignConfirm.descLine1')}<br/>
              {t('settings.pets.assignConfirm.descLine2')}
            </p>

            <div className="flex gap-3">
              <button 
                onClick={() => setAssignConfirmation(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleAssignPets}
                className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition-colors shadow-lg shadow-purple-200"
              >
                {t('settings.pets.assignConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Import Confirmation Modal */}
    {/* Import modal removed — single family mode */}

    {/* System Template Import Modal */}
    {isSystemTemplateModalOpen && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => {
            if (isImportingSystemTemplate) return;
            setIsSystemTemplateModalOpen(false);
          }}
        ></div>
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-6">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <Plus className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">
              {t('settings.items.importSystemTemplateModal.title')}
            </h3>
            <p className="text-gray-500 text-sm mb-4 text-center">
              {t('settings.items.importSystemTemplateModal.desc')}
            </p>

            <div className="mb-5">
              <div className="text-xs text-gray-500 mb-2 text-center">{t('settings.items.importSystemTemplateModal.ageRangeLabel')}</div>
              <div className="grid grid-cols-3 gap-2">
                {(['3-5', '6-9', '10+'] as const).map((range) => (
                  <button
                    key={range}
                    type="button"
                    onClick={() => setSystemTemplateAgeRange(range)}
                    disabled={isImportingSystemTemplate}
                    className={`py-2 rounded-xl text-sm font-bold transition-all border ${
                      systemTemplateAgeRange === range
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    } ${isImportingSystemTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {t(`settings.items.importSystemTemplateModal.ageRange_${range.replace('+', 'plus')}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsSystemTemplateModalOpen(false)}
                disabled={isImportingSystemTemplate}
                className={`flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-colors ${isImportingSystemTemplate ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleImportSystemTemplate}
                disabled={isImportingSystemTemplate}
                className={`flex-1 py-2.5 text-white rounded-xl font-bold shadow-lg transition-colors ${isImportingSystemTemplate ? 'bg-gray-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'}`}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  {isImportingSystemTemplate && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isImportingSystemTemplate ? t('common.loading') : t('settings.items.importSystemTemplateModal.confirm')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Reset Class Confirmation Modal */}
    {resetClassConfirmation && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => {
             if (isResettingClass) return;
             setResetClassConfirmation(false);
          }}
        ></div>
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-6">
            <div className="flex items-center gap-4 mb-6">
               <div className="w-12 h-12 rounded-xl bg-red-100 text-red-500 flex items-center justify-center shrink-0">
                 <AlertTriangle className="w-6 h-6" />
               </div>
               <div>
                 <h3 className="text-xl font-bold text-gray-800">{t('settings.pets.reset.title')}</h3>
                 <p className="text-gray-500 text-sm mt-0.5">
                   {currentClass?.name} · {t('settings.account.studentsCount', { count: students.length })}
                 </p>
               </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Cleared Items */}
              <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                <h4 className="font-bold text-red-800 mb-3">{t('settings.pets.reset.cleared')}</h4>
                <ul className="space-y-2 text-sm text-red-600 font-medium">
                  <li>{t('settings.pets.reset.clearedItems.points')}</li>
                  <li>{t('settings.pets.reset.clearedItems.petSelection')}</li>
                  <li>{t('settings.pets.reset.clearedItems.badges')}</li>
                  <li>{t('settings.pets.reset.clearedItems.coins')}</li>
                  <li>{t('settings.pets.reset.clearedItems.growthRecords')}</li>
                  <li>{t('settings.pets.reset.clearedItems.exchangeRecords')}</li>
                </ul>
              </div>

              {/* Kept Items */}
              <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                <h4 className="font-bold text-green-800 mb-3">{t('settings.pets.reset.kept')}</h4>
                <ul className="space-y-2 text-sm text-green-600 font-medium">
                  <li>{t('settings.pets.reset.keptItems.scoreRules')}</li>
                  <li>{t('settings.pets.reset.keptItems.levelRules')}</li>
                  <li>{t('settings.pets.reset.keptItems.className')}</li>
                  <li>{t('settings.pets.reset.keptItems.groups')}</li>
                  <li>{t('settings.pets.reset.keptItems.shopConfig')}</li>
                </ul>
              </div>
            </div>

            <div className="bg-orange-50 text-orange-800 text-sm font-bold p-4 rounded-xl border border-orange-100 mb-6 text-center">
              {t('settings.pets.reset.warning')}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                 <Lock className="w-4 h-4" /> {t('settings.pets.reset.passwordLabel')}
              </label>
              <input
                type="password"
                value={resetClassPassword}
                onChange={(e) => {
                  setResetClassPassword(e.target.value);
                  setResetClassError('');
                }}
                placeholder={t('settings.pets.reset.passwordPlaceholder')}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/10 transition-all bg-gray-50 focus:bg-white"
                disabled={isResettingClass}
              />
              {resetClassError && (
                <div className="text-sm text-red-600 mt-2 font-bold flex items-center gap-1">
                   <X className="w-4 h-4" /> {resetClassError}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button 
                onClick={() => setResetClassConfirmation(false)}
                className="w-32 py-3 border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-colors"
                disabled={isResettingClass}
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={handleResetClass}
                disabled={isResettingClass || !resetClassPassword || resetCountdown > 0}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isResettingClass ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {t('settings.pets.reset.confirm')}
                    {resetCountdown > 0 && ` (${resetCountdown}s)`}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Check-in Task Delete Confirmation */}
    {checkinDeleteConfirm && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => !isCheckinDeleting && setCheckinDeleteConfirm(null)}
        ></div>
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('checkin.deleteTitle')}</h3>
            <p className="text-gray-500 text-sm mb-6">
              {t('checkin.deleteConfirm', { name: checkinDeleteConfirm.name })}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setCheckinDeleteConfirm(null)}
                disabled={isCheckinDeleting}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDeleteCheckinTask}
                disabled={isCheckinDeleting}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-200 disabled:opacity-60"
              >
                {isCheckinDeleting ? t('common.delete') + '...' : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Delete Confirmation Modal */}
    {deleteConfirmation && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setDeleteConfirmation(null)}
        ></div>
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">{t('settings.students.deleteConfirm.title')}</h3>
            <p className="text-gray-500 text-sm mb-6">
              {t('settings.students.deleteConfirm.descPrefix')}
              <span className="font-bold text-gray-800">{deleteConfirmation.name}</span>
              {t('settings.students.deleteConfirm.descSuffix')}<br/>
              {t('settings.students.deleteConfirm.descLine2')}
            </p>
            <div className="flex gap-3">
              <button 
                onClick={() => setDeleteConfirmation(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={() => handleDeleteStudent(deleteConfirmation.id)}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-200"
              >
                {t('settings.students.deleteConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Delete Class Modal */}
    {classDeleteConfirmation && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => {
            if (isClassDeleting) return;
            setClassDeleteConfirmation(null);
            setClassDeletePassword('');
            setClassDeleteError('');
          }}
        ></div>
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="p-6">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-500 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2 text-center">{t('settings.class.deleteConfirm.title')}</h3>
            <p className="text-gray-500 text-sm mb-5 text-center">
              {t('settings.class.deleteConfirm.descPrefix')}
              <span className="font-bold text-gray-800">{classDeleteConfirmation.name}</span>
              {t('settings.class.deleteConfirm.descSuffix')}<br/>
              {t('settings.class.deleteConfirm.descLine2')}
            </p>

            <label className="block text-sm font-bold text-gray-700 mb-2">{t('settings.class.deleteConfirm.passwordLabel')}</label>
            <input
              type="password"
              value={classDeletePassword}
              onChange={(e) => setClassDeletePassword(e.target.value)}
              placeholder={t('settings.class.deleteConfirm.passwordPlaceholder')}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-red-500 bg-white"
              disabled={isClassDeleting}
            />
            {classDeleteError && (
              <div className="text-sm text-red-600 mt-2">{classDeleteError}</div>
            )}

            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  if (isClassDeleting) return;
                  setClassDeleteConfirmation(null);
                  setClassDeletePassword('');
                  setClassDeleteError('');
                }}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isClassDeleting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteClass}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isClassDeleting || !classDeletePassword.trim()}
              >
                {isClassDeleting ? t('settings.class.deleteConfirm.deleting') : t('settings.class.deleteConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Password Change Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsPasswordModalOpen(false)}
          ></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 className="text-xl font-black text-gray-800 flex items-center gap-2">
                <span className="text-2xl">🔐</span> {t('settings.password.title')}
              </h3>
              <button 
                onClick={() => setIsPasswordModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">{t('settings.password.currentPassword.label')}</label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t('settings.password.currentPassword.placeholder')}
                    className="w-full pl-12 pr-12 py-3 bg-white border-2 border-gray-100 rounded-xl focus:border-indigo-500 outline-none transition-colors"
                  />
                  <button
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                  💡 {t('settings.password.currentPassword.hint')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">{t('settings.password.newPassword.label')}</label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('settings.password.newPassword.placeholder')}
                    className="w-full pl-12 pr-12 py-3 bg-white border-2 border-gray-100 rounded-xl focus:border-indigo-500 outline-none transition-colors"
                  />
                  <button
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400 flex items-center gap-1">
                  💡 {t('settings.password.newPassword.hint')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">{t('settings.password.confirmPassword.label')}</label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('settings.password.confirmPassword.placeholder')}
                    className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-100 rounded-xl focus:border-indigo-500 outline-none transition-colors"
                  />
                </div>
              </div>

              {passwordError && (
                <div className="p-3 bg-red-50 text-red-600 text-sm font-medium rounded-xl flex items-center gap-2">
                  <span className="text-lg">🚫</span> {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="p-3 bg-green-50 text-green-600 text-sm font-medium rounded-xl flex items-center gap-2">
                  <span className="text-lg">✅</span> {passwordSuccess}
                </div>
              )}

              <button
                onClick={handleChangePassword}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-lg shadow-indigo-200 transition-all active:scale-95"
              >
                {t('settings.password.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Icon Picker Modal */}
      {showIconPicker && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowIconPicker(false)}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg h-[500px] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
               <div className="flex items-center gap-2 text-lg font-bold text-gray-800">
                 <Search className="w-5 h-5 text-purple-500" />
                 {t('settings.iconPicker.title')}
               </div>
               <button onClick={() => setShowIconPicker(false)} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 transition-colors">
                 <X className="w-5 h-5" />
               </button>
            </div>
            
            {/* Categories */}
            <div className="px-4 pt-4 pb-2 flex gap-2 overflow-x-auto scrollbar-hide">
              {ICON_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveIconCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                    activeIconCategory === cat.id
                      ? 'bg-purple-600 text-white shadow-lg shadow-purple-200'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {t(cat.nameKey)}
                </button>
              ))}
            </div>

            {/* Icons Grid */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
               <div className="grid grid-cols-6 gap-3">
                 {ICON_CATEGORIES.find(c => c.id === activeIconCategory)?.icons.map(iconName => {
                   const Icon = ICON_MAP[iconName];
                   if (!Icon) return null;
                   const isSelected = pickerTarget === 'new' ? newItemIcon === iconName : editingItem?.icon === iconName;
                   
                   return (
                     <button
                       key={iconName}
                       onClick={() => {
                         if (pickerTarget === 'new') {
                           setNewItemIcon(iconName);
                         } else if (editingItem) {
                           setEditingItem({ ...editingItem, icon: iconName });
                         }
                         setShowIconPicker(false);
                       }}
                       className={`aspect-square rounded-xl flex items-center justify-center border-2 transition-all ${
                         isSelected
                           ? 'border-purple-500 bg-purple-50 text-purple-600 shadow-md scale-110'
                           : 'border-transparent hover:bg-gray-50 text-gray-400 hover:text-gray-600'
                       }`}
                     >
                       <Icon className="w-6 h-6" />
                     </button>
                   );
                 })}
               </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-pointer"
            onClick={() => setPreviewImage(null)}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-4">
            <button 
              onClick={() => setPreviewImage(null)} 
              className="absolute top-2 right-2 p-1.5 bg-gray-100/80 hover:bg-gray-200 rounded-full text-gray-500 transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <img src={previewImage} alt="Preview" className="max-w-[80vw] max-h-[80vh] object-contain drop-shadow-xl" />
          </div>
        </div>
      )}
    </>
  );
}
